import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { Product as ProductType } from '../services/adminService';

// Mock data - in real app, this would come from API
const mockProducts = [
  {
    id: 1,
    name: 'Fresh Tomatoes',
    price: 150,
    unit: 'per kg',
    farmer: 'John Mwangi',
    location: 'Nakuru',
    image: '🍅',
    rating: 4.8,
    reviews: 24,
    description: 'Fresh, ripe tomatoes from our organic farm in Nakuru',
    category: 'Vegetables'
  },
  {
    id: 2,
    name: 'Green Bananas',
    price: 80,
    unit: 'per bunch',
    farmer: 'Mary Wanjiku',
    location: 'Meru',
    image: '🍌',
    rating: 4.6,
    reviews: 18,
    description: 'Fresh green bananas perfect for cooking',
    category: 'Fruits'
  },
  {
    id: 3,
    name: 'Fresh Kale',
    price: 50,
    unit: 'per bunch',
    farmer: 'Peter Kimani',
    location: 'Kiambu',
    image: '🥬',
    rating: 4.9,
    reviews: 32,
    description: 'Organic kale, pesticide-free and fresh',
    category: 'Vegetables'
  },
  {
    id: 4,
    name: 'Avocados',
    price: 120,
    unit: 'per piece',
    farmer: 'Grace Akinyi',
    location: 'Murang\'a',
    image: '🥑',
    rating: 4.7,
    reviews: 28,
    description: 'Creamy avocados from the highlands',
    category: 'Fruits'
  },
  {
    id: 5,
    name: 'Sweet Potatoes',
    price: 100,
    unit: 'per kg',
    farmer: 'David Otieno',
    location: 'Kisumu',
    image: '🍠',
    rating: 4.5,
    reviews: 15,
    description: 'Sweet and nutritious orange sweet potatoes',
    category: 'Tubers'
  },
  {
    id: 6,
    name: 'Onions',
    price: 200,
    unit: 'per kg',
    farmer: 'Jane Muthoni',
    location: 'Nyeri',
    image: '🧅',
    rating: 4.4,
    reviews: 22,
    description: 'Fresh red onions, perfect for cooking',
    category: 'Vegetables'
  }
];

const Products: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const { addToCart, isInCart } = useCart();
  const toast = useToast();

  const categories = ['All', 'Vegetables', 'Fruits', 'Tubers', 'Grains'];

  const handleAddToCart = (product: any) => {
    // Convert mock product to Product type
    const cartProduct: ProductType = {
      _id: product.id.toString(),
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      stock: 100, // Mock stock
      images: [], // Mock images
      farmer: product.farmer,
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    addToCart(cartProduct, 1);
    toast.success(`${product.name} added to cart!`);
  };
  
  const filteredProducts = mockProducts.filter(product => {
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.farmer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Fresh Produce</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Browse our selection of fresh, quality produce from verified farmers across Kenya
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products or farmers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <div className="aspect-square bg-gray-100 flex items-center justify-center text-6xl">
                {product.image}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {product.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                <div className="flex items-center mb-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`h-4 w-4 ${
                          i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="ml-1 text-sm text-gray-600">
                      {product.rating} ({product.reviews})
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-2xl font-bold text-gray-900">KSh {product.price}</span>
                    <span className="text-sm text-gray-500 ml-1">/{product.unit}</span>
                  </div>
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  <p>👨‍🌾 {product.farmer}</p>
                  <p>📍 {product.location}</p>
                </div>
                <div className="flex gap-2">
                  <Link
                    to={`/products/${product.id}`}
                    className="flex-1 bg-gray-100 text-gray-700 text-center py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    View Details
                  </Link>
                  <button
                    onClick={() => handleAddToCart(product)}
                    className={`flex-1 ${
                      isInCart(product.id.toString())
                        ? 'bg-green-100 text-green-700 border border-green-600'
                        : 'bg-gradient-to-r from-green-600 to-green-800 text-white'
                    } text-center py-2 px-4 rounded-lg hover:opacity-90 transition-all font-medium flex items-center justify-center space-x-1`}
                  >
                    {isInCart(product.id.toString()) ? (
                      <>
                        <span>✓</span>
                        <span>In Cart</span>
                      </>
                    ) : (
                      <>
                        <span>🛒</span>
                        <span>Add</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
